import React from 'react';

import { Drawer, Input, Pagination, Tag, Typography } from 'antd';
import type { PaginationProps } from 'antd';
import { useIntl } from 'react-intl';

import BoxContainer from '@src/components/BoxContainer';
import BoxTitle from '@src/components/BoxContainer/components/Title';
import FilterContainer from '@src/components/FilterContainer';

import styles from './styles.module.less';

const { Text } = Typography;

export const DRILLDOWN_TABLE_MAX_HEIGHT = 'calc(100dvh - 300px)';

export interface DrilldownDrawerProps {
  open: boolean;
  title: string;
  /** Count line under the header, e.g. "12 orders". */
  summary: string;
  /** Optional chip describing the active range/filter. */
  tag?: string;
  search: {
    value: string;
    placeholder: string;
    onChange: (value: string) => void;
  };
  onClose: () => void;
  /** Usually a table with the drilled-down rows. */
  children: React.ReactNode;
  pagination?: Pick<PaginationProps, 'current' | 'disabled' | 'onChange' | 'pageSize' | 'total'>;
  width?: number;
}

/** Side drawer listing the records behind a KPI or chart segment, with free-text search. */
const DrilldownDrawer: React.FC<DrilldownDrawerProps> = ({
  open,
  title,
  summary,
  tag,
  search,
  onClose,
  children,
  pagination,
  width = 880,
}) => {
  const intl = useIntl();
  const showPagination = Boolean(
    pagination && pagination.total && pagination.total > (pagination.pageSize ?? 0),
  );

  return (
    <Drawer
      className={styles.drawer}
      closable={false}
      destroyOnClose
      footer={null}
      open={open}
      title={null}
      width={width}
      onClose={onClose}
    >
      <BoxContainer className={styles.layout}>
        <BoxContainer content fixed bottomBorder>
          <FilterContainer
            title={<BoxTitle.Header value={title} />}
            goBack={onClose}
          />
        </BoxContainer>
        <BoxContainer content className={styles.body}>
          {open && (
            <>
              <div className={styles.summary}>
                <Text type="secondary">{summary}</Text>
                {tag && <Tag color="blue">{tag}</Tag>}
              </div>
              <Input.Search
                allowClear
                className={styles.search}
                aria-label={search.placeholder}
                placeholder={search.placeholder}
                value={search.value}
                onChange={event => search.onChange(event.target.value)}
              />
              <div className={styles.table}>{children}</div>
              {showPagination && pagination && (
                <div className={styles.pagination}>
                  <Pagination
                    {...pagination}
                    aria-label={intl.formatMessage({ id: 'general.pagination.label' })}
                    hideOnSinglePage
                    responsive
                    showLessItems
                    showSizeChanger={false}
                    showTotal={(total, [from, to]) =>
                      intl.formatMessage(
                        { id: 'general.pagination.footer' },
                        { from, to, total },
                      )}
                    size="small"
                  />
                </div>
              )}
            </>
          )}
        </BoxContainer>
      </BoxContainer>
    </Drawer>
  );
};

export default DrilldownDrawer;
